import React, { useState } from "react";
import PropTypes from "prop-types";
import { Card, CardBody, CardHeader } from "reactstrap";

const MRRGrowthChart = ({ data = [] }) => {
    const [hoveredIdx, setHoveredIdx] = useState(null);

    if (!data || data.length < 2) {
        return (
            <Card
                className="neo-stat-card h-100 animate__animated animate__fadeIn"
                style={{
                    borderRadius: "16px",
                    border: "2px solid #482712",
                    boxShadow: "0 15px 40px rgba(72, 39, 18, 0.08)",
                    background: "#fff",
                    fontFamily: "'Outfit', 'Inter', sans-serif"
                }}
            >
                <CardHeader className="p-4 bg-transparent border-bottom-0 pb-1">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-2">
                            <div
                                style={{
                                    width: "38px",
                                    height: "38px",
                                    minWidth: "38px",
                                    borderRadius: "10px",
                                    background: "rgba(226, 102, 28, 0.08)",
                                    border: "1px solid rgba(226, 102, 28, 0.20)",
                                    color: "#e2661c",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center"
                                }}
                            >
                                <i className="mdi mdi-chart-line font-size-18"></i>
                            </div>
                            <div>
                                <h5 className="mb-0 fw-bold font-size-14 text-uppercase" style={{ color: "#482712", letterSpacing: "0.5px" }}>
                                    Receita
                                </h5>
                                <small className="text-muted font-size-11 fw-semibold d-block">
                                    Evolução do faturamento recorrente mensal (Últimos 12 meses)
                                </small>
                            </div>
                        </div>
                    </div>
                </CardHeader>
                <CardBody className="p-4 d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "330px" }}>
                    <div className="text-center text-muted">
                        <i className="mdi mdi-trending-up font-size-36 mb-2 d-block" style={{ color: "rgba(72, 39, 18, 0.2)" }}></i>
                        <span className="font-size-12 fw-semibold">Sem dados históricos de faturamento suficientes</span>
                    </div>
                </CardBody>
            </Card>
        );
    }

    const chartData = data;

    // Calcular valores de pico para escala do gráfico (eixo Y começando do absoluto 0)
    const values = chartData.map(d => d.mrr);
    const minVal = 0; // Começa do absoluto 0 para a linha começar mais abaixo e mostrar proporção real
    const maxVal = Math.max(...values) * 1.1; // Dá respiro superior
    const valRange = maxVal - minVal || 1;

    // Cores outonais Premium
    const COLORS = {
        primary: "#e2661c",     // Laranja ativo
        lineShadow: "rgba(226, 102, 28, 0.3)",
        grid: "rgba(72, 39, 18, 0.06)",
        text: "#482712"         // Chocolate
    };

    // Converter dados para pontos coordenados SVG (base 100x100)
    const points = chartData.map((item, idx) => ({
        x: (100 / Math.max(1, chartData.length - 1)) * idx,
        y: 100 - ((item.mrr - minVal) / valRange) * 100
    }));

    // Algoritmo de Spline Suavizado (Catmull-Rom)
    const buildSmoothPath = (pts) => {
        if (pts.length === 0) return "";
        if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
        const tension = 0.2;

        let path = `M ${pts[0].x} ${pts[0].y}`;

        for (let i = 0; i < pts.length - 1; i++) {
            const p0 = pts[Math.max(0, i - 1)];
            const p1 = pts[i];
            const p2 = pts[i + 1];
            const p3 = pts[Math.min(pts.length - 1, i + 2)];

            const cp1x = p1.x + (p2.x - p0.x) * tension;
            const cp1y = p1.y + (p2.y - p0.y) * tension;
            const cp2x = p2.x - (p3.x - p1.x) * tension;
            const cp2y = p2.y - (p3.y - p1.y) * tension;

            path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
        }

        return path;
    };

    const smoothPath = buildSmoothPath(points);

    // Área fechada para o preenchimento de degradê inferior
    const areaPath = points.length > 0
        ? `${smoothPath} L ${points[points.length - 1].x} 100 L ${points[0].x} 100 Z`
        : "";

    // Função para calcular o crescimento percentual em relação ao mês anterior
    const getGrowthPercentage = (idx) => {
        if (idx === 0) return null;
        const prev = chartData[idx - 1].mrr;
        const curr = chartData[idx].mrr;
        const diff = ((curr - prev) / prev) * 100;
        return diff.toFixed(1);
    };

    return (
        <Card
            className="neo-stat-card h-100 animate__animated animate__fadeIn"
            style={{
                borderRadius: "16px",
                border: "2px solid #482712",
                boxShadow: "0 15px 40px rgba(72, 39, 18, 0.08)",
                background: "#fff",
                fontFamily: "'Outfit', 'Inter', sans-serif"
            }}
        >
            <CardHeader className="p-4 bg-transparent border-bottom-0 pb-1">
                <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                        <div
                            style={{
                                width: "38px",
                                height: "38px",
                                minWidth: "38px",
                                borderRadius: "10px",
                                background: "rgba(226, 102, 28, 0.08)",
                                border: "1px solid rgba(226, 102, 28, 0.20)",
                                color: "#e2661c",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        >
                            <i className="mdi mdi-chart-line font-size-18"></i>
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bold font-size-14 text-uppercase" style={{ color: "#482712", letterSpacing: "0.5px" }}>
                                Receita
                            </h5>
                            <small className="text-muted font-size-11 fw-semibold d-block">
                                Evolução do faturamento recorrente mensal (Últimos 12 meses)
                            </small>
                        </div>
                    </div>
                    <div>
                        <span className="badge bg-success-subtle text-success fw-bold font-size-11 px-2.5 py-1.5 rounded-pill">
                            +45.1% Total no Ano
                        </span>
                    </div>
                </div>
            </CardHeader>
            <CardBody className="p-4">
                <div style={{ overflowX: "auto", overflowY: "hidden", paddingTop: "50px", paddingBottom: "10px" }} className="custom-scroll">
                    <div style={{ height: "275px", position: "relative", minWidth: "550px", width: "100%", paddingRight: "10px" }}>
                        <div className="h-100 w-100 d-flex position-relative align-items-end" style={{ zIndex: 1, paddingBottom: "52px" }}>

                            {/* Linhas de Grade de Fundo */}
                            <div className="position-absolute w-100 h-100 d-flex flex-column justify-content-between" style={{ pointerEvents: "none", bottom: "52px", height: "calc(100% - 52px)", zIndex: 1 }}>
                                <div style={{ borderBottom: `1px dashed ${COLORS.grid}`, width: "100%" }} />
                                <div style={{ borderBottom: `1px dashed ${COLORS.grid}`, width: "100%" }} />
                                <div style={{ borderBottom: `1px dashed ${COLORS.grid}`, width: "100%" }} />
                                <div style={{ borderBottom: `1px dashed ${COLORS.grid}`, width: "100%" }} />
                            </div>

                            {/* SVG de Linhas e Área de Degradê */}
                            <svg
                                style={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    width: "100%",
                                    height: "calc(100% - 52px)",
                                    pointerEvents: "none",
                                    zIndex: 4
                                }}
                                preserveAspectRatio="none"
                                viewBox="0 0 100 100"
                            >
                                <defs>
                                    <linearGradient id="mrrAreaGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor={COLORS.primary} stopOpacity="0.35" />
                                        <stop offset="100%" stopColor={COLORS.primary} stopOpacity="0.00" />
                                    </linearGradient>
                                    <filter id="mrrLineShadow" x="-20%" y="-20%" width="140%" height="140%">
                                        <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor={COLORS.primary} floodOpacity="0.3" />
                                    </filter>
                                </defs>

                                {/* Área Preenchida com Degradê */}
                                {areaPath && (
                                    <path d={areaPath} fill="url(#mrrAreaGradient)" />
                                )}

                                {/* Linha de Evolução Principal */}
                                {smoothPath && (
                                    <path
                                        d={smoothPath}
                                        fill="none"
                                        stroke={COLORS.primary}
                                        strokeWidth="3.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        vectorEffect="non-scaling-stroke"
                                        filter="url(#mrrLineShadow)"
                                    />
                                )}
                            </svg>

                            {/* Eixos Horizontais e Controle de Hover */}
                            {chartData.map((item, idx) => {
                                const isHovered = hoveredIdx === idx;
                                const pointY = points[idx] ? 100 - points[idx].y : 0;
                                const growth = getGrowthPercentage(idx);

                                return (
                                    <div
                                        key={idx}
                                        className="flex-grow-1 h-100 d-flex flex-column align-items-center justify-content-end position-relative"
                                        onMouseEnter={() => setHoveredIdx(idx)}
                                        onMouseLeave={() => setHoveredIdx(null)}
                                        style={{ cursor: "crosshair", zIndex: 5 }}
                                    >
                                        {/* Linha Vertical no Hover */}
                                        {isHovered && (
                                            <div
                                                className="position-absolute"
                                                style={{
                                                    height: "calc(100% - 52px)",
                                                    width: "1.5px",
                                                    borderLeft: `1.5px dashed rgba(226, 102, 28, 0.4)`,
                                                    bottom: "52px",
                                                    left: "50%",
                                                    transform: "translateX(-50%)",
                                                    pointerEvents: "none",
                                                    zIndex: 0
                                                }}
                                            />
                                        )}

                                        {/* Ponto Dourado/Laranja de Foco */}
                                        {isHovered && points[idx] && (
                                            <div
                                                className="position-absolute shadow"
                                                style={{
                                                    bottom: `calc(${pointY}% * (1 - 52px / 100%) + 52px)`,
                                                    width: "12px",
                                                    height: "12px",
                                                    borderRadius: "50%",
                                                    left: "50%",
                                                    backgroundColor: "#fff",
                                                    border: `3.5px solid ${COLORS.primary}`,
                                                    transform: "translate(-50%, 50%)",
                                                    zIndex: 20,
                                                    pointerEvents: "none"
                                                }}
                                            />
                                        )}

                                        {/* Tooltip flutuante personalizado */}
                                        {isHovered && (
                                            <div
                                                className="position-absolute bg-white shadow-lg rounded-3 p-2 text-center"
                                                style={{
                                                    top: "-42px",
                                                    width: "160px",
                                                    left: "55%",
                                                    transform: "translateX(-50%)",
                                                    pointerEvents: "none",
                                                    zIndex: 9999,
                                                    border: "1.5px solid #482712"
                                                }}
                                            >
                                                <div className="fw-bold font-size-10 text-uppercase text-muted mb-1">{item.month}</div>
                                                <div className="fw-bold font-size-14" style={{ color: COLORS.primary }}>
                                                    R$ {item.mrr.toLocaleString("pt-BR", { minimumFractionDigits: 0 })}
                                                </div>
                                                {growth && (
                                                    <span className={`font-size-9 fw-semibold d-inline-block mt-1 ${Number(growth) >= 0 ? "text-success" : "text-danger"}`}>
                                                        <i className={`mdi ${Number(growth) >= 0 ? "mdi-arrow-up-bold" : "mdi-arrow-down-bold"} me-0.5`} />
                                                        {growth}% vs mês ant.
                                                    </span>
                                                )}
                                            </div>
                                        )}

                                        {/* Label do mês no Eixo X */}
                                        <span
                                            className="position-absolute fw-bold transition-all"
                                            style={{
                                                bottom: "-45px",
                                                fontSize: "10px",
                                                color: isHovered ? COLORS.primary : COLORS.text,
                                                letterSpacing: "0.2px"
                                            }}
                                        >
                                            {item.month}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </CardBody>
        </Card>
    );
};

MRRGrowthChart.propTypes = {
    data: PropTypes.arrayOf(PropTypes.shape({
        month: PropTypes.string.isRequired,
        mrr: PropTypes.number.isRequired
    }))
};

export default MRRGrowthChart;
